import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Typography } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import Send from '@material-ui/icons/Send';
import { List } from 'immutable';
import { createElement, FC, MouseEventHandler, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import CreateModificationRequest from '../../../../entity/CreateModificationRequest';
import EditModificationRequestItem from '../../../../entity/EditModificationRequestItem';
import Language from '../../../../entity/Language';
import ProductPricing from '../../../../entity/ProductPricing';
import UploadingFile from '../../../../entity/UploadingFile';
import UploadingFilesProgress from '../../../../entity/UploadingFilesProgress';
import {
  CreationDetailFragment,
  DeliverableForCraetionDetailFragment,
  SingleDirectorUserFragment,
} from '../../../../generated/graphql';
import { getLatestDeliverable } from '../../../../graphql/creation';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import ButtonWithIcon from '../../primitive/ButtonWithIcon';
import MediaPlayer from '../MediaPlayer';
import CreatingModificationRequestButton from './CreatingModificationRequestButton';
import InputCancelDialog from './InputCancelDialog';
import _Item from './Item';
import ModificationRequestSendingDialog from './ModificationRequestSendingDialog';

interface Props {
  creation: CreationDetailFragment;
  createModificationRequest: CreateModificationRequest;
  editModificationRequestItem: EditModificationRequestItem;
  deliverable: DeliverableForCraetionDetailFragment;
  files: Array<FileReference>;
  bucketId: string | null;
  // Craete
  uploadingAssetFiles: Array<UploadingFile>;
  uploadingFilesProgress: UploadingFilesProgress;
  onRequestUploadFiles: (files: UploadingFile[]) => void;
  onRemoveAssetFile: (bucketId: string, fileId: string) => void;
  onRemoveAllAssetFile: () => void;
  onPushInputtingItem: (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => Promise<void>;
  onRequestRemoveItem: (modificationRequestItemId: string, index: number) => void;
  // Edit
  onRemoveEditAssetFile: (fileId: string) => void;
  onOpenEditModificationRequestDialog: (index: number) => void;
  onCloseEditModificationRequestDialog: () => void;
  onSubmitEditModificationRequestDialog: (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    duration: string,
    comment: string,
  ) => void;
  onRequestEditAssetFiles: (files: UploadingFile[], bucketId: string) => void;

  onClickCancelButton: MouseEventHandler<HTMLElement>;
  onClickSendButton: () => Promise<void>;
  onRefetchCreation: () => void;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  shortagePoints: number;
  organizationId: string;
  email: string;
  language: Language;
  onPurchasedPoint: (point: number) => Promise<void>;
  prepareUser: () => Promise<SingleDirectorUserFragment | void>;
  className?: string;
}

const CreatingForm: FC<Props> = ({
  creation,
  createModificationRequest,
  editModificationRequestItem,
  deliverable,
  files,
  bucketId,
  uploadingAssetFiles,
  uploadingFilesProgress,
  onRequestUploadFiles,
  onRemoveAssetFile,
  onRemoveAllAssetFile,
  onPushInputtingItem,
  onRequestRemoveItem,
  onRemoveEditAssetFile,
  onOpenEditModificationRequestDialog,
  onCloseEditModificationRequestDialog,
  onSubmitEditModificationRequestDialog,
  onRequestEditAssetFiles,
  onClickCancelButton,
  onClickSendButton,
  onRefetchCreation,
  isPointRequired,
  shortagePoints,
  className,
}) => {
  const [isSendDialogOpen, setSendDialog] = useState<boolean>(false);
  const [isCancelDialogOpen, setCancelDialog] = useState<boolean>(false);
  const [isButtonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [isSavingItem, setIsSavingItem] = useState<boolean>(false);

  useEffect(() => {
    setButtonDisabled(
      createModificationRequest.items.some((item) => item.body.trim().length === 0) ||
        createModificationRequest.items.length === 0,
    );
  }, [createModificationRequest.items]);

  const isNeedFee = createModificationRequest.feeType === 'fee';

  const latestDeliverable = getLatestDeliverable(creation);
  const itemEls = List(
    createModificationRequest.items
      .map((item, key) => (
        <Item
          item={item}
          deliverable={latestDeliverable}
          editModificationRequestItem={editModificationRequestItem}
          files={files}
          bucketId={createModificationRequest.bucketId}
          index={key}
          onClickRemoveButton={() => onRequestRemoveItem(item.id, key)}
          onRemoveEditAssetFile={onRemoveEditAssetFile}
          onOpenEditModificationRequestDialog={onOpenEditModificationRequestDialog}
          onCloseEditModificationRequestDialog={onCloseEditModificationRequestDialog}
          onSubmitEditModificationRequestDialog={async (
            index: number,
            editModificationRequestItem: EditModificationRequestItem,
            modificationRequestItemId: string,
            duration: string,
            comment: string,
          ) => {
            setIsSavingItem(true);
            await onSubmitEditModificationRequestDialog(
              index,
              editModificationRequestItem,
              modificationRequestItemId,
              duration,
              comment,
            );
            setIsSavingItem(false);
          }}
          onRequestEditAssetFiles={onRequestEditAssetFiles}
          key={key}
          disabled={isSavingItem}
        />
      ))
      .values(),
  );

  const AddButton = (
    <CreatingModificationRequestButton
      createModificationRequest={createModificationRequest}
      deliverable={deliverable}
      uploadingAssetFiles={uploadingAssetFiles}
      uploadingFilesProgress={uploadingFilesProgress}
      bucketId={bucketId}
      files={files}
      onRequestUploadFiles={onRequestUploadFiles}
      onRemoveAssetFile={onRemoveAssetFile}
      onRemoveAllAssetFile={onRemoveAllAssetFile}
      onPushInputtingItem={onPushInputtingItem}
    />
  );

  const onClickSendEvent = () => setSendDialog(true);

  return (
    <Root className={className}>
      <DeliverableScreen>
        <VideoOrImage aspectRatio={[4, 3]}>
          <MediaPlayer mimeType={deliverable.creative.file.mimeType} url={deliverable.creative.file.url} />
        </VideoOrImage>
      </DeliverableScreen>

      <ItemsWithButton>
        {itemEls.size > 0 ? <Items>{itemEls}</Items> : null}

        <AddCommentButton>{AddButton}</AddCommentButton>

        <Buttons>
          <CancelButton
            icon={Delete}
            color="default"
            variant="contained"
            onClick={(e) => {
              createModificationRequest.items.length > 0 ? setCancelDialog(true) : onClickCancelButton(e);
            }}
            disabled={isSavingItem}
          >
            <FormattedMessage id="modification_request_creating_card.cancel" />
          </CancelButton>

          <SendButton
            icon={Send}
            color="primary"
            variant="contained"
            disabled={
              isButtonDisabled ||
              isSavingItem ||
              (createModificationRequest.feeType === 'fee' && isPointRequired && shortagePoints < 0)
            }
            onClick={() => onClickSendEvent()}
          >
            <FormattedMessage id="modification_request_creating_card.send" />
          </SendButton>
        </Buttons>

        {createModificationRequest.feeType === 'fee' && isPointRequired && shortagePoints < 0 ? (
          <ShortageMessage>
            <FormattedMessage id="project_editing_page.point_shortage_message" />
          </ShortageMessage>
        ) : null}

        <InputCancelDialog
          isCancelDialogOpen={isCancelDialogOpen}
          setCancelDialog={setCancelDialog}
          onClickCancelButton={onClickCancelButton}
        />

        <ModificationRequestSendingDialog
          onClickAcceptButton={() => {
            setSendDialog(false);
            onClickSendButton().then(() => onRefetchCreation());
          }}
          onRequestClose={() => setSendDialog(false)}
          isNeedFee={isNeedFee}
          open={isSendDialogOpen}
        />
      </ItemsWithButton>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['40%', '60%'],
    rows: ['auto'],
    areas: [['deliverable', 'itemsWithButton']],
  }),
  {
    width: 'auto',
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
});

const VideoOrImage = styled(AspectRatioFixed)(gridLayoutItem('video', { alignSelf: 'flex-start' }), {
  marginBottom: SPACING_REGULAR,
});

const ItemsWithButton = styled('div')(gridLayoutItem('itemsWithButton'));

const Items = styled('div')(
  gridLayoutItem('items'),
  flexibleLayoutContainer({
    direction: 'column',
  }),
  { marginBottom: SPACING_REGULAR },
);

const Item = styled(_Item)({
  marginBottom: SPACING_UNIT,
  border: `1px solid ${theme.palette.grey['200']}`,
  ':last-child': {
    marginBottom: 0,
  },
});

const AddCommentButton = styled('div')(
  flexibleLayoutContainer({
    justifyContent: 'center',
  }),
);

const Buttons = styled('div')(
  flexibleLayoutContainer({ alignItems: 'center', justifyContent: 'flex-end' }),
  gridLayoutItem('buttons', { justifySelf: 'flex-end' }),
  {
    marginBottom: SPACING_REGULAR,
  },
);
const CancelButton = styled(ButtonWithIcon)({ marginRight: SPACING_REGULAR });
const SendButton = styled(ButtonWithIcon)({});

const ShortageMessage = styled(Typography)`
  margin-top: 8px;
  color: #ff7170;
`;

export default CreatingForm;
